/**
 * InlinePreview — Renders the override preview inline on the EID view page.
 *
 * Reuses the same API calls and rendering logic as /override-preview,
 * but receives overrideRequest + goldenId as props instead of URL params.
 */
import React, { useState, useEffect, useCallback } from "react";
import FHIRSummary from "../../components/FHIRSummary";
import SearchRecordTable from "../../routes/NewSearchRecords/SearchRecordTable";
import { useAuthState } from "../../components/AuthProvider";
import handleResponse from "../../utils/handleFetchResponse";
import fetchConfig from "../../utils/fetchConfig";
import fhirBundleParser from "../../utils/fhirBundleParser";
import { useMessageToast } from "../../components/MessageProvider";
import Loader from "../../components/Loader";
import "../../routes/Preview/Preview.css";

export default function InlinePreview({ overrideRequest, goldenId, onExit }) {
  const toast = useMessageToast();
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const userId = user.username;

  const [loading, setLoading] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [priorAggregate, setPriorAggregate] = useState(null);
  const [isFocusDeleted, setIsFocusDeleted] = useState(false);
  const [destinationGoldenId, setDestinationGoldenId] = useState(null);
  const [movedRecords, setMovedRecords] = useState([]);
  const [deletedAggregatePrior, setDeletedAggregatePrior] = useState(null);
  const [primaryRecords, setPrimaryRecords] = useState([]);
  const [rqFhirBundleData, setRqFhirBundleData] = useState([]);
  const [error, setError] = useState(null);

  const fetchPreview = useCallback(() => {
    if (!goldenId || !overrideRequest) return;
    setLoading(true);
    setError(null);

    let sourceId = "";
    try {
      if (overrideRequest.patients?.[0]) {
        const firstPatient = JSON.parse(overrideRequest.patients[0]);
        sourceId = firstPatient?.meta?.source || "";
      }
    } catch (e) {
      console.error("Error parsing patient for sourceId:", e);
    }

    const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patientsource/override`;
    const config = {
      method: "PUT",
      body: JSON.stringify(overrideRequest, null, 0),
      credentials: "include",
      headers: {
        clientId: selectedClient.oid,
        "Content-Type": "application/json",
        userId: userId,
        preview: true,
        sourceId: sourceId,
      },
    };

    fetch(...fetchConfig(url, "/overridePreviewMock.json", config))
      .then(handleResponse)
      .then((response) => {
        const rawData = response.data?.map || response.data;

        const aggregatesToDeleteList =
          rawData?.aggregatesToDelete?.myArrayList || [];
        const focusDeleted = aggregatesToDeleteList.some((item) =>
          item.endsWith(`|${goldenId}`),
        );
        setIsFocusDeleted(focusDeleted);

        const aggregatesList = rawData?.aggregates?.myArrayList || [];
        let destinationAgg = null;
        let deletedAgg = null;
        for (const agg of aggregatesList) {
          const aggMap = agg.map;
          if (aggMap.golden_id === goldenId && focusDeleted) {
            deletedAgg = aggMap;
          } else {
            destinationAgg = aggMap;
          }
        }
        if (!focusDeleted && aggregatesList.length > 0) {
          for (const agg of aggregatesList) {
            const aggMap = agg.map;
            if (aggMap.golden_id === goldenId) {
              destinationAgg = aggMap;
            }
          }
        }

        let newIdentity = null;
        if (destinationAgg?.demographics) {
          try {
            newIdentity = JSON.parse(destinationAgg.demographics);
          } catch (e) {
            console.error("Error parsing destination demographics:", e);
          }
        }
        setIdentity(newIdentity);
        setDestinationGoldenId(destinationAgg?.golden_id || null);

        const prevDemos =
          rawData?.previousDemographicsByAggregateId?.myArrayList || [];
        let priorForDestination = null;
        let priorForDeleted = null;
        for (const item of prevDemos) {
          const keys = Object.keys(item.map);
          for (const key of keys) {
            try {
              if (destinationAgg && key === destinationAgg.aggregate_id) {
                priorForDestination = JSON.parse(item.map[key]);
              }
              if (key.endsWith(`|${goldenId}`)) {
                priorForDeleted = JSON.parse(item.map[key]);
              }
            } catch (e) {
              console.error("Error parsing prior demographics:", e);
            }
          }
        }
        setPriorAggregate(
          focusDeleted
            ? priorForDestination
            : priorForDeleted || priorForDestination,
        );
        setDeletedAggregatePrior(priorForDeleted);

        if (focusDeleted && deletedAgg?.old_records?.myArrayList) {
          setMovedRecords(deletedAgg.old_records.myArrayList.map((r) => r.map));
        } else {
          setMovedRecords([]);
        }

        const recordUpdatesList = rawData?.recordUpdates?.myArrayList || [];
        const recordUpdatesByRecordId = {};
        const recordUpdateFhirPatients = [];
        for (const ru of recordUpdatesList) {
          const rec = ru.map;
          const recKey = `${rec.source_system}|${rec.source_entity_id}`;
          try {
            const fhirPatient =
              typeof rec.demographics === "string"
                ? JSON.parse(rec.demographics)
                : rec.demographics;
            recordUpdatesByRecordId[recKey] = {
              fhirPatient,
              goldenId: rec.golden_id,
            };
            recordUpdateFhirPatients.push(fhirPatient);
          } catch (e) {
            console.error("Error parsing recordUpdate demographics:", e);
          }
        }

        const targetGoldenId = focusDeleted
          ? destinationAgg?.golden_id
          : goldenId;

        if (targetGoldenId && Object.keys(recordUpdatesByRecordId).length > 0) {
          const matcherUrl = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patient/match?include_related_identities=false`;
          const matcherBody = {
            patient: {
              resourceType: "Patient",
              link: [
                {
                  other: { reference: targetGoldenId },
                  type: "seealso",
                },
              ],
            },
          };
          const matcherConfig = {
            method: "POST",
            body: JSON.stringify(matcherBody),
            credentials: "include",
            headers: {
              clientId: selectedClient.oid,
              "Content-Type": "application/json",
              userId: userId,
            },
          };

          fetch(...fetchConfig(matcherUrl, "/closeMatches.json", matcherConfig))
            .then(handleResponse)
            .then((matcherResponse) => {
              const matcherData = matcherResponse.data;
              let matcherRecords = [];
              if (
                matcherData?.resourceType === "Bundle" &&
                matcherData?.total > 1
              ) {
                const parsed = fhirBundleParser(matcherData);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  matcherRecords = parsed[0].records || [];
                }
              }

              const mergedRecords = [];
              const usedKeys = new Set();
              for (const [recKey, ruEntry] of Object.entries(
                recordUpdatesByRecordId,
              )) {
                mergedRecords.push(ruEntry.fhirPatient);
                usedKeys.add(recKey);
              }
              for (const mr of matcherRecords) {
                const mrKey = `${mr.meta?.source}|${mr.id}`;
                if (!usedKeys.has(mrKey)) {
                  mergedRecords.push(mr);
                }
              }

              setPrimaryRecords(mergedRecords);
              setLoading(false);
            })
            .catch((matcherErr) => {
              console.warn(
                "Matcher API call failed, using recordUpdates only:",
                matcherErr,
              );
              setPrimaryRecords(recordUpdateFhirPatients);
              setLoading(false);
            });
        } else {
          setPrimaryRecords(recordUpdateFhirPatients);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error("InlinePreview fetch error:", e);
        setError("Something went wrong while fetching the preview.");
        setLoading(false);
        toast.error("Something went wrong while fetching the preview");
      });
  }, [goldenId, overrideRequest, selectedClient.oid, userId, toast]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  // Build rqFhirBundleData when primaryRecords change
  useEffect(() => {
    if (primaryRecords.length === 0) {
      setRqFhirBundleData([]);
      return;
    }
    const targetId = isFocusDeleted ? destinationGoldenId : goldenId;
    const safeRecords = primaryRecords.map((r) => ({
      ...r,
      score: r.score ?? "",
      scoreString: r.scoreString ?? "",
    }));
    setRqFhirBundleData([
      { id: targetId, records: safeRecords, table: "primary" },
    ]);
  }, [primaryRecords, goldenId, destinationGoldenId, isFocusDeleted]);

  return (
    <div>
      {/* ── Sticky Preview Banner ── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#fffbeb",
          border: "1px solid #fbbf24",
          borderRadius: "8px",
          padding: "14px 20px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          boxShadow: "0 2px 12px rgba(251, 191, 36, 0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: "20px" }}>👁️</span>
          <div>
            <div
              style={{ fontWeight: 700, fontSize: "14px", color: "#92400e" }}
            >
              Preview Mode
            </div>
            <div
              style={{ fontSize: "13px", color: "#a16207", marginTop: "2px" }}
            >
              This is a preview of the override changes. No changes have been
              applied yet.
            </div>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              marginLeft: "8px",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                background: "#dcfce7",
                border: "1px solid #86efac",
                marginRight: "4px",
              }}
            />
            <span style={{ fontSize: "12px", color: "#92400e" }}>
              New/Changed
            </span>
          </span>
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
          >
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                background: "#fee2e2",
                border: "1px solid #fca5a5",
                marginRight: "4px",
              }}
            />
            <span style={{ fontSize: "12px", color: "#92400e" }}>Original</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onExit}
          style={{
            cursor: "pointer",
            borderRadius: "9999px",
            border: "1px solid #dc2626",
            background: "#dc2626",
            color: "#ffffff",
            padding: "6px 16px",
            fontSize: "13px",
            fontWeight: 600,
            boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#b91c1c";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#dc2626";
          }}
        >
          ✕ Exit Preview
        </button>
      </div>

      <Loader loading={loading}>
        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #f87171",
              borderRadius: "8px",
              padding: "16px",
              color: "#991b1b",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>{error}</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={fetchPreview}
                style={{
                  cursor: "pointer",
                  borderRadius: "6px",
                  border: "1px solid #f87171",
                  background: "#ffffff",
                  color: "#dc2626",
                  padding: "4px 12px",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                Retry
              </button>
              <button
                type="button"
                onClick={onExit}
                style={{
                  cursor: "pointer",
                  borderRadius: "6px",
                  border: "none",
                  background: "#dc2626",
                  color: "#ffffff",
                  padding: "4px 12px",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                Exit Preview
              </button>
            </div>
          </div>
        )}

        {identity && (
          <>
            {isFocusDeleted && (
              <div className="preview-migration-banner">
                <div className="preview-migration-header">
                  <span className="preview-migration-icon">⚠️</span>
                  <strong>Aggregate Migration Detected</strong>
                </div>
                <p className="preview-migration-detail">
                  EID <strong>{goldenId}</strong> will be{" "}
                  <span className="preview-deleted-badge">deleted</span>. All
                  records are moving to EID{" "}
                  <strong>{destinationGoldenId}</strong>.
                </p>
                {movedRecords.length > 0 && (
                  <div className="preview-moved-records">
                    <div className="preview-moved-records-title">
                      Records migrating to EID {destinationGoldenId}:
                    </div>
                    <table className="preview-records-table">
                      <thead>
                        <tr>
                          <th>Source System</th>
                          <th>Record ID</th>
                          <th>Destination EID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movedRecords.map((rec, idx) => (
                          <tr key={idx}>
                            <td>
                              <code>{rec.source_system}</code>
                            </td>
                            <td>
                              <code>{rec.source_entity_id}</code>
                            </td>
                            <td>{rec.new_golden_id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {isFocusDeleted && deletedAggregatePrior && (
              <div className="preview-deleted-aggregate-section">
                <div className="preview-deleted-aggregate-header">
                  <span className="preview-deleted-badge">Deleted</span>
                  Prior state of EID {goldenId}
                </div>
                <FHIRSummary
                  id={goldenId}
                  patientInfo={deletedAggregatePrior}
                  primaryRecords={[]}
                  linkageHistory={[]}
                  handleRecompute={() => {}}
                  canRecompute={false}
                  isInvisible={true}
                  handleRepartition={() => {}}
                  handleMultipleBirth={() => {}}
                  handleAggregateAuxDataRequest={() => {}}
                  isImdm={true}
                />
              </div>
            )}

            <div
              className={isFocusDeleted ? "preview-destination-section" : ""}
            >
              {isFocusDeleted && (
                <div className="preview-destination-header">
                  Destination EID {destinationGoldenId} — After Override
                </div>
              )}
              <FHIRSummary
                id={isFocusDeleted ? destinationGoldenId : goldenId}
                patientInfo={identity}
                priorAggregate={priorAggregate}
                primaryRecords={primaryRecords}
                linkageHistory={[]}
                handleRecompute={() => {}}
                canRecompute={false}
                isInvisible={true}
                handleRepartition={() => {}}
                handleMultipleBirth={() => {}}
                handleAggregateAuxDataRequest={() => {}}
                isImdm={true}
                isPreviewMode={true}
              />
            </div>

            {rqFhirBundleData.length > 0 && (
              <SearchRecordTable
                rq_gid={isFocusDeleted ? destinationGoldenId : goldenId}
                rq_aggregate={identity}
                is_review_queue_page={true}
                rqFhirBundleData={rqFhirBundleData}
                isPreviewMode={true}
              />
            )}
          </>
        )}
      </Loader>
    </div>
  );
}
